import { useEffect, useState } from 'react';

import { NodeTrees } from 'ui/treeView';
import { useFieldsFilterContext } from '../filter';
import { useUserMetadataQueryContext } from '../../contexts/UserMetadataQueryContext';

type Data = NodeTrees | undefined;

/** Ensures that special characters do not break search */
const escapeSpecialCharacters = (text: string) => text.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&');

const isTextMatch = (searchText: string, text: string, isCaseSensitive = false): boolean => {
  const searchRegex = new RegExp(escapeSpecialCharacters(searchText), isCaseSensitive ? 'g' : 'gi');
  const matched = Array.from(text.matchAll(searchRegex), (match) => match[0]);

  return matched.length > 0;
};

const searchCriteria = (query: string, fields: Data): boolean => {
  // Don't bother matching if no search query or no fields
  if (!fields) {
    return false;
  }

  if (query === '') {
    return true;
  }

  return Boolean(
    Object.entries(fields).find(([key, value]) => {
      const match = isTextMatch(query, `${key}`) || isTextMatch(query, `${value}`);

      if (match) {
        return match;
      }

      if (typeof value === 'object') {
        return searchCriteria(query, value as Data);
      }

      return match;
    }),
  );
};

export const useFieldsSearch = () => {
  const { dataFields, isError, isLoading } = useUserMetadataQueryContext();
  const { search } = useFieldsFilterContext();

  const [isMatch, searchForMatch] = useState<boolean>(search === '');

  useEffect(() => {
    const fieldSearch = searchCriteria(search, dataFields);
    searchForMatch(fieldSearch);
  }, [dataFields, search]);

  return {
    data: dataFields,
    isMatch,
    isLoading,
    isError,
  };
};
